import { FC, useContext, useEffect, useMemo, useState } from "react";

import { ActionButton } from "@hdi-seguros/web-components.action-button";

import { Form } from "antd";

import dayjs from "dayjs";

import { InfoContext, UIContext } from "../../../shared/contexts";

import CurrentAccountService from "../../../shared/services/currentAccount";
import AuthenticationService from "../../../shared/services/authentication";

import { GetWithholdingDetailListServiceResponse, Withholding } from "../../../shared/interfaces/services/withholding-detail";
import { GetClientBalanceListServiceResponse } from "../../../shared/interfaces/services/client-balance";
import { FILE_TYPES, InputFactoryProp, Intermediary } from "../../../shared/interfaces";

import { PageHeader } from "../../../shared/components/MainLayout/components";
import { InputFactory, RowSize } from "../../../shared/components";
import { WithholdingTable } from "./components/WitholdingDetailTable";

import userHelper from "../../../shared/utils/userHelper";
import { downloadBase64Files } from "../../../shared/utils";

import './index.css'
import { DownloadOutlined } from "@ant-design/icons";
import { MonthsValuesEnum } from "../../../shared/interfaces/enums/monts-values-enum";

interface WithholdingDetailProps {
    title: string;
}

export const WithholdingDetail: FC<WithholdingDetailProps> = ({ title }) => {
    const { isLoading, setIsLoading, setShowError } = useContext( UIContext );
    const { setTitle } = useContext(InfoContext);

    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [showErrorBalance, setShowErrorBalance] = useState(false);

    const [selectedItems, setSelectedItems] = useState<Withholding[]>([]);
    const [ withholdingDetailList, setWithholdingDetailList ] = useState<GetWithholdingDetailListServiceResponse>({});    
    const [ clientBalance, setClientBalance ] = useState<GetClientBalanceListServiceResponse>({});
    const [isDownloading, setIsDownloading] = useState(false);

    const [form] = Form.useForm()
    const [producersCombo, setProducersCombo] = useState<any>([]);
    const [producersInfo, setProducersInfo] = useState<Array<Intermediary>>();
    const [selectedProducer, setSelectedProducer] = useState<Intermediary>();
    const userInfo = userHelper.getUser();
    
    const getClientBalance = async () => {
        
        if (!producersInfo) return;
        
        let selectedProducerOption: Intermediary | undefined;
        
        setShowError(false);
        const filters = form.getFieldsValue();
        try {
            if (producersInfo) {
                selectedProducerOption = producersInfo.find(
                    (producer) => producer.codigoInter === filters.producer
                );
                setSelectedProducer(selectedProducerOption);
            }
            if(filters.producer !== selectedProducer?.codigoInter) {
                setShowErrorBalance(false);
                const responseClientBalance = await CurrentAccountService.getClientBalance(selectedProducerOption);
                if (responseClientBalance.saldo) {
                    setClientBalance({ ...responseClientBalance });
                }
            }
        } catch (error) {
            setShowErrorBalance(true);
        }

        setIsLoading(true);
        try {
            const response = await CurrentAccountService.getWithholdingDetail(filters, selectedProducerOption!);
            if (response.retenciones) {
                response.retenciones.forEach((retencion) => {
                    retencion.anioymes = `${MonthsValuesEnum[parseInt(retencion.mes)]} - ${retencion.anio}`;
                });                
                setWithholdingDetailList({ ...response });
            }
        } catch (error) {
            setShowError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setTitle(title);
        /* eslint-disable-next-line */
    }, [title]);

    const firstDay: string = dayjs().subtract(1, 'month').format('DD-MM-YYYY');
    const dateFormat: string = 'DD-MM-YYYY';

    const initialStateFilters = useMemo(() => ({
        producer: userInfo?.intermediarioSecundario?.codigoInter,
        date: [dayjs(firstDay, dateFormat), dayjs()],
    }), []);

    const getDetailForm = async () => {
        try {
          await getComboProducer();
          setIsLoadingFilters(false);
        } catch (error) {}
    };

    useEffect(() => {
        getDetailForm();
        /* eslint-disable-next-line */
    }, []);

    useEffect(() => {        
        getClientBalance();
    }, [producersInfo]);
    
    
    const getComboProducer = async () => {
        try {
            const response = await AuthenticationService.getIntermediariesByIntermediary();
            setProducersInfo(response.data.intermediarios.intermediario);
            let comboProducersData = response.data.intermediarios.intermediario.map((producer: any) => ({
                value: producer.codigoInter,
                label: `${producer.nombreInter} (${producer.nivelInter}-${producer.codigoInter})`,
            }));
            
            if (response.status === 200){
                setProducersCombo(comboProducersData);
            } 
        } catch (error) {}
    };

    const inputsFilter: InputFactoryProp[] = [
        {
            element: 'select',
            placeHolder: 'Productor',
            items: producersCombo,
            allowClear: false,
            name: 'producer',
        },
        {
            element: 'date',
            name: 'date',
        },
    ];

    const handleSelectionChange = (selectedRows: Withholding[]) => {
        setSelectedItems(selectedRows);
    };

    const downloadFiles = async () => {    
        if (selectedItems.length === 0) {            
            return;
        }
        setIsDownloading(true);      
        try {
            const generatedFiles = await CurrentAccountService.postDownloadWiholdingDetailsFiles(selectedItems, selectedProducer!);        
            generatedFiles.forEach(file => {
                downloadBase64Files(file.base64, file.fileName, FILE_TYPES.PDF)
            });
        } catch (error) {} finally {
            
            setIsDownloading(false);
        }
    };

    return (
        <div className="withholding-list">
            <PageHeader actions={undefined} />
            <RowSize rowProps={{ gutter: [0, 0] }} className="withholding-list__filter-row">
                <InputFactory 
                    form={form}
                    items={ inputsFilter }
                    onFilter={ getClientBalance }
                    isLoading={ isLoadingFilters }
                    initialState={ initialStateFilters }
                />

            </RowSize>
            {
                <div className="withholding-list__table-container" >
                    <ActionButton loading={isDownloading} disabled={selectedItems.length === 0} label="Procesar Selección" icon={<DownloadOutlined />} type="secondary" className="withholding-list__proccess-button btn-accent-outline-barbuss" onClick={downloadFiles} />
                    <WithholdingTable                 
                        withholdingList={withholdingDetailList}
                        isLoading={isLoading}
                        selectedProducer={selectedProducer}
                        onSelectionChange={handleSelectionChange} 
                    />
                </div> 
            }
        </div>
    )
}